import React, { useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Typography, notification, Tag } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AppSelect } from '../../../constants/appSelect';
import { newObjectKeys } from '../../../utils/utility';
import {
  deletePositionVideoRequest,
  getListPositionVideosRequest,
} from '../../../services/lichvietvideo/positionVideoServiceRequest';
import { getAllDisplayPageRequest } from '../../../services/lichvietvideo/commonServiceRequest';
import isEqual from 'lodash/isEqual';
import { min } from 'lodash';
const { Text } = Typography;

const QuanLyViTri = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  const {
    data: dataPositionVideos,
    isPending,
    refetch: refetchDataPositions,
  } = useQuery({ queryKey: ['dataPositionVideos', params], queryFn: () => getListPositionVideosRequest(params) });

  // get data all display page
  const { data: dataAllDisplayPages } = useQuery({
    queryKey: ['dataAllDisplayPages'],
    queryFn: () => getAllDisplayPageRequest(),
  });

  // convert list position options
  const dataAllDisplayPagesOption = useMemo(() => {
    return dataAllDisplayPages?.data?.map((item) => {
      return { label: item?.name, value: item?.name };
    });
  }, [dataAllDisplayPages]);

  const { mutate: mutateDeletePositionVideo } = useMutation({
    mutationFn: deletePositionVideoRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Xóa vị trí thành công!',
        });
        refetchDataPositions();
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.display_page) {
      Object.assign(temp, { display_page: values?.display_page });
    }
    if (values?.status >= 0) {
      Object.assign(temp, { status: [values?.status] });
    }
    if (!isEqual(temp, params)) {
      setParams(temp);
      return
    } 
    refetchDataPositions();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: 'Vị trí hiển thị',
      dataIndex: ['id', 'display_page'],
      key: 'id',
      render: (_, { display_page, id }) => (
        <Link to={`/lv-video/quan-ly-vi-tri/${id}/sua-position`} className="text-[#1677FF]">
          {display_page}
        </Link>
      ),
      width: 400,
      fixed: 'left',
    },
    {
      title: 'Thời gian tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_, { created_at }) => (
        <Space direction="vertical">{created_at && <Text className="text-[#667085]">{created_at}</Text>}</Space>
      ),
      minWidth: 120,
      width: 150,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={AppSelect.STATUS?.find((item) => item?.value === status)?.color}>
          {AppSelect.STATUS?.find((item) => item?.value === status)?.label}
        </Tag>
      ),
      width: 120,
      fixed: 'right',
    },

    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`/lv-video/quan-ly-vi-tri/${record?.id}/sua-position`)}>
            <EditOutlined />
          </Button>
          <Button type="text" onClick={() => mutateDeletePositionVideo({ id: record?.id })}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
      width: 90,
      fixed: 'right',
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách vị trí</h1>
        <Link to={'/lv-video/quan-ly-vi-tri/them-position'}>
          <Button type="primary">
            <PlusOutlined />
            Thêm vị trí
          </Button>
        </Link>
      </div>

      <Form
        name="search_position_video"
        form={formSearch}
        layout="vertical"
        requiredMark={false}
        className="mb-5"
        onFinish={handleSubmitForm}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="display_page" label={<Text>Vị trí hiển thị</Text>}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={dataAllDisplayPagesOption}
                allowClear
                placeholder="Chọn vị trí hiển thị"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...AppSelect.STATUS]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params && Object.values(newObjectKeys(params, ['display_page', 'status'])).some((value) => value) && (
              <Button
                htmlType="button"
                onClick={() => {
                  formSearch.resetFields();
                  setParams({ page: 1, limit: params.limit });
                }}
              >
                <DeleteOutlined />
                Xóa lọc
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <TableCustom
        columns={columns}
        dataSource={dataPositionVideos?.data}
        totalItem={dataPositionVideos?.total}
        params={params}
        setParams={setParams}
      />
    </div>
  );
};

export default QuanLyViTri;
